import styled, { css } from 'styled-components';

import { ListCardStyle } from 'types';
import {
  bodyRegular,
  bodySemibold,
  captionSemibold,
} from 'styles/mixins/fonts';

// Rows are grids like the app's list cards: every column a proportional share
// of the row, 24px between columns, 24px inset (+1px border, so header, rows
// and total share the same column edges).
const gridRow = css<{ $template: string }>`
  display: grid;
  column-gap: 24px;
  align-items: center;
  padding: 0 24px;
  border: 1px solid transparent;
  grid-template-columns: ${({ $template }) => $template};
`;

export const Grid = styled.div`
  gap: 0;
  display: flex;
  flex-direction: column;
  ${({ theme }) => css`
    color: ${theme.config.table[ListCardStyle.Default].default.text};
  `}
`;

// Same header type as the app's lists (ListHeader): 14/24 semibold, grey.
export const HeadRow = styled.div<{ $template: string }>`
  ${gridRow};
  padding-bottom: 8px;
`;

const kindStyles = css`
  min-width: 0;

  &[data-kind='amount'] {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  &[data-kind='date'],
  &[data-kind='nowrap'] {
    white-space: nowrap;
  }

  &[data-kind='flex'],
  &[data-kind='compact'] {
    overflow-wrap: anywhere;
  }
`;

export const HeadCell = styled.div`
  ${bodySemibold};
  ${kindStyles};
  overflow: hidden;
  white-space: nowrap;
  color: ${({ theme }) => theme.config.table.head.title};
`;

export const Cell = styled.div`
  ${bodyRegular};
  ${kindStyles};
  overflow: hidden;

  &[data-kind='icon'] {
    display: flex;
    overflow: visible;
  }
`;

export const GridRow = styled.div<{
  $odd?: boolean;
  $template: string;
  $clickable?: boolean;
}>`
  ${gridRow};
  min-height: 72px;
  padding-top: 24px;
  padding-bottom: 24px;
  border-radius: 16px;
  ${({ $odd, theme, $clickable }) => css`
    background: ${$odd
      ? theme.config.table[ListCardStyle.Grey].default.bg
      : theme.config.table[ListCardStyle.Default].default.bg};

    ${$clickable &&
    css`
      cursor: pointer;

      &:hover {
        background: ${$odd
          ? theme.config.table[ListCardStyle.Grey].hover.bg
          : theme.config.table[ListCardStyle.Default].hover.bg};
      }

      &:focus {
        outline: none;
      }

      &:focus-visible {
        box-shadow: inset 0 0 0 2px ${theme.colors.blue.primary.standard};
      }
    `}
  `}
`;

export const TotalRow = styled.div<{ $template: string }>`
  ${gridRow};
  ${bodySemibold};
  margin-top: 8px;
  padding-top: 24px;
  padding-bottom: 24px;
  border-radius: 16px;
  ${({ theme }) => css`
    border: ${theme.config.table[ListCardStyle.Default].selected.border};
  `}
`;

export const TotalLabelCell = styled.div<{ $span: number }>`
  min-width: 0;
  grid-column: ${({ $span }) => `1 / span ${$span}`};
`;

// The muted dash for an empty value.
export const Empty = styled.span`
  color: ${({ theme }) => theme.config.table.head.title};
`;

// Phone: one card per record.
export const Cards = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

// Phone: 16px inset. A stacked table on a wider screen keeps the table's
// text inset (24px + 1px border), so the drawer title stays aligned.
export const Card = styled.div<{ $odd?: boolean; $clickable?: boolean }>`
  gap: 12px;
  display: flex;
  padding: 16px;

  ${({ theme }) =>
    !theme.responsive.isMobile &&
    css`
      padding: 24px;
      border: 1px solid transparent;
    `}

  border-radius: 16px;
  flex-direction: column;
  ${({ $odd, theme, $clickable }) => css`
    background: ${$odd
      ? theme.config.table[ListCardStyle.Grey].default.bg
      : theme.config.table[ListCardStyle.Default].default.bg};

    ${$clickable &&
    css`
      cursor: pointer;

      &:focus {
        outline: none;
      }

      &:focus-visible {
        box-shadow: inset 0 0 0 2px ${theme.colors.blue.primary.standard};
      }
    `}
  `}
`;

export const TotalCard = styled(Card)`
  ${({ theme }) => css`
    border: ${theme.config.table[ListCardStyle.Default].selected.border};
  `}
`;

export const CardTop = styled.div`
  ${bodySemibold};
  gap: 16px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  > :last-child {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

export const CardField = styled.div<{ $center?: boolean }>`
  gap: 16px;
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  ${({ $center }) =>
    $center &&
    css`
      align-items: center;

      > :first-child {
        padding-top: 0 !important;
      }

      > :last-child {
        display: flex;
        align-items: center;
      }
    `}

  > :first-child {
    ${captionSemibold};
    padding-top: 4px;
    color: ${({ theme }) => theme.config.table.head.title};
  }

  > :last-child {
    ${bodyRegular};
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

// The receipt button in a table row, centred like the row's text.
export const IconWrap = styled.span`
  display: flex;
  align-items: center;
`;
